/**
 * `ExternalCvCertificationDetail` — dettaglio di una certificazione importata da
 * Credly, associata a un profilo CV esterno. È l'equivalente di
 * `TeamMemberCertificationDetail` per i profili esterni: mostra la medaglietta e
 * i dati di assegnazione (origine Credly, date, badge) del profilo indicato.
 *
 * Rotta `/external-cv/:id/certifications/:certificationId` (sezione "Livelli",
 * protetta lato API da `requireLevelsAccess`).
 * Dati: `GET /api/external-cv/profiles/:id/certifications/:certificationId`
 * → `MyCertificationDetailDto` (404 se il profilo non possiede la certificazione).
 */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import type { MyCertificationDetailDto } from '../shared/dto';
import { certExpiryStatus, formatCertDate } from '../components/CertificationCard';
import { CertificationMedal } from '../components/CertificationMedal';
import { Alert, Badge, Button, Icon, Loading, PageHeader } from '../components/ui';

/** Renderizza la scadenza con lo stesso stato/tono della card certificazione. */
function ExpiryValue({ iso }: { iso: string | null }) {
  const status = certExpiryStatus(iso);
  if (status === 'none') return <>Nessuna</>;
  if (status === 'expired') {
    return <Badge tone="danger">{formatCertDate(iso)}</Badge>;
  }
  if (status === 'expiring') {
    return <Badge tone="warning">In scadenza {formatCertDate(iso)}</Badge>;
  }
  return <>{formatCertDate(iso)}</>;
}

export default function ExternalCvCertificationDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { id, certificationId } = useParams<{
    id: string;
    certificationId: string;
  }>();

  const { data, isLoading, isError, error } = useQuery<MyCertificationDetailDto>({
    queryKey: ['externalCv', 'profiles', id, 'certifications', 'detail', certificationId],
    queryFn: () =>
      api.get<MyCertificationDetailDto>(
        `/api/external-cv/profiles/${encodeURIComponent(id ?? '')}/certifications/${encodeURIComponent(certificationId ?? '')}`,
      ),
    enabled:
      id !== undefined &&
      id !== '' &&
      certificationId !== undefined &&
      certificationId !== '',
  });

  const backToProfile = () => {
    void navigate(`/external-cv/${encodeURIComponent(id ?? '')}`);
  };

  if (isLoading) {
    return <Loading>Caricamento certificazione…</Loading>;
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error ? error.message : 'Impossibile caricare la certificazione.';
    return (
      <section
        className="certification-detail"
        aria-labelledby="external-cv-certification-detail-title"
      >
        <PageHeader
          title="Dettaglio certificazione"
          titleId="external-cv-certification-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToProfile}>
              ← Torna al profilo
            </Button>
          }
        />
        <Alert tone="error">{message}</Alert>
      </section>
    );
  }

  return (
    <section
      className="certification-detail"
      aria-labelledby="external-cv-certification-detail-title"
    >
      <PageHeader
        title="Dettaglio certificazione"
        titleId="external-cv-certification-detail-title"
        subtitle="I dettagli della certificazione e dell'assegnazione del profilo."
        actions={
          <Button type="button" variant="ghost" onClick={backToProfile}>
            ← Torna al profilo
          </Button>
        }
      />

      {/* Hero: medaglietta, nome, ente, descrizione, skill e link alla pagina
          generale della certificazione. */}
      <div className="certification-detail__hero">
        <CertificationMedal
          imageUrl={data.imageUrl}
          name={data.name}
          issuer={data.issuer}
          backFields={[
            { label: 'Conseguita', value: formatCertDate(data.issuedAt) },
            {
              label: 'Scadenza',
              value:
                data.expiresAt === null ? 'Nessuna' : formatCertDate(data.expiresAt),
            },
          ]}
        />
        <div className="certification-detail__heading">
          <h2 className="certification-detail__name">{data.name}</h2>
          {data.issuer !== undefined && (
            <p className="certification-detail__issuer">{data.issuer}</p>
          )}
          {data.description !== undefined && (
            <p className="certification-detail__description">{data.description}</p>
          )}
          {data.skills.length > 0 && (
            <div className="certification-detail__skills">
              {data.skills.map((skill) => (
                <Badge key={skill.id} tone="neutral">
                  {skill.name}
                </Badge>
              ))}
            </div>
          )}
          {data.url !== undefined && (
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="certification-detail__link"
            >
              <Icon name="external" />
              <span>Vedi la certificazione</span>
            </a>
          )}

          {/* Assegnazione del profilo: date, origine e link al badge specifico. */}
          <dl className="certification-detail__meta">
            <div className="certification-detail__meta-item">
              <dt>Conseguita</dt>
              <dd>{formatCertDate(data.issuedAt)}</dd>
            </div>
            <div className="certification-detail__meta-item">
              <dt>Scadenza</dt>
              <dd>
                <ExpiryValue iso={data.expiresAt} />
              </dd>
            </div>
            <div className="certification-detail__meta-item">
              <dt>Origine</dt>
              <dd>
                {data.source === 'credly' ? (
                  <Badge tone="info">Credly</Badge>
                ) : (
                  <Badge tone="neutral">Manuale</Badge>
                )}
              </dd>
            </div>
            {data.badgeUrl !== undefined && (
              <div className="certification-detail__meta-item">
                <dt>Badge</dt>
                <dd>
                  <a
                    href={data.badgeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="certification-detail__link"
                  >
                    <Icon name="external" />
                    <span>Apri su Credly</span>
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </section>
  );
}
